import React, { useState, useCallback, useEffect } from "react";
import { ProColumns, ProTable } from "@ant-design/pro-components";
import { Button, Typography, message, Popconfirm, Alert, Select } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";
import { getVocabList, archiveVocab } from "./api/VocabAPI";
import {
  IVocab,
  IAntDProTableRequest,
  IAPIResponse,
} from "./type/VocabManagementType";
import { debounce } from "lodash";
import { getDictionaryList } from "../dictionary/api/DictionaryAPI"; // Add this import
import { IDictionary } from "../dictionary/type/DictionaryManagementType";

const { Title } = Typography;

const VocabManagement: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // parse URLSearchParams to queryParams
  const parseSearchParams = (search: string): IAntDProTableRequest => {
    const params = new URLSearchParams(search);
    return {
      params: {
        current: parseInt(params.get("page") || "1"),
        pageSize: 100,
      },
      filter: {
        dictionaryId: params.get("dictionaryId") || undefined,
        wordIndexId: params.get("wordIndexId") || undefined,
        hanji: params.get("hanji") || undefined,
        roman: params.get("roman") || undefined,
        category: params.get("category") || undefined,
        subCategory: params.get("subCategory") || undefined,
      },
    };
  };

  // convert queryParams to URLSearchParams string
  const toSearchParams = (queryParams: IAntDProTableRequest): string => {
    const params = new URLSearchParams();

    if (queryParams.params.current) {
      params.set("page", queryParams.params.current.toString());
    }

    if (queryParams.filter) {
      Object.entries(queryParams.filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, value.toString());
        }
      });
    }

    return params.toString();
  };

  const [queryParams, setQueryParams] = useState<IAntDProTableRequest>(
    parseSearchParams(location.search)
  );

  const [selectedDictionaryId, setSelectedDictionaryId] = useState<
    string | undefined
  >(queryParams.filter?.dictionaryId);

  useEffect(() => {
    const searchString = toSearchParams(queryParams);

    // remove '?'
    const currentSearch = location.search.substring(1);

    if (searchString !== currentSearch) {
      navigate(`${location.pathname}?${searchString}`, { replace: true });
    }
  }, [queryParams, navigate, location.pathname]);

  // Fetch dictionary list
  const { data: dictionaryData } = useQuery({
    queryKey: ["dictionaryList"],
    queryFn: () => getDictionaryList({ params: { pageSize: 1000 } }),
  });

  // Fetch selected dictionary details
  const { data: selectedDictionary } = useQuery({
    queryKey: ["dictionary", selectedDictionaryId],
    queryFn: () => getDictionaryList({ params: { id: selectedDictionaryId } }),
    enabled: !!selectedDictionaryId,
  });

  useEffect(() => {
    if (selectedDictionary?.results?.length === 1) {
      const dictionary = selectedDictionary.results[0];
      setQueryParams((prev) => ({
        ...prev,
        filter: { ...prev.filter, dictionaryId: dictionary.id },
      }));
    }
  }, [selectedDictionary]);

  const { data, isLoading, error } = useQuery<IAPIResponse, Error>({
    queryKey: ["vocabList", queryParams],
    queryFn: () => getVocabList(queryParams),
    gcTime: 10 * 60 * 1000, // Cache is kept for 10 minutes (garbage collection time)
  });

  const archiveMutation = useMutation({
    mutationFn: archiveVocab,
    onSuccess: () => {
      message.success("Vocabulary archived successfully.");
      queryClient.invalidateQueries({ queryKey: ["vocabList"] });
    },
    onError: (error) => {
      console.error("Error archiving vocabulary:", error);
      message.error("Failed to archive vocabulary.");
    },
  });

  const handleArchive = (id: string) => {
    archiveMutation.mutate(id);
  };

  const debouncedSetQueryParams = useCallback(
    debounce((newParams: Partial<IAntDProTableRequest>) => {
      setQueryParams((prev) => ({
        ...prev,
        ...newParams,
        params: { ...prev.params, current: 1 }, // Reset to first page on filter change
      }));
    }, 300),
    []
  );

  useEffect(() => {
    return () => {
      debouncedSetQueryParams.cancel();
    };
  }, [debouncedSetQueryParams]);

  const columns: ProColumns<IVocab>[] = [
    {
      title: "Entry ID",
      dataIndex: "wordIndexId",
      key: "wordIndexId",
      initialValue: queryParams.filter?.wordIndexId,
      fieldProps: {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          debouncedSetQueryParams({
            filter: { ...queryParams.filter, wordIndexId: e.target.value },
          }),
      },
    },
    {
      title: "Source",
      dataIndex: ["dictionary", "name"],
      key: "dictionaryId",
      initialValue: queryParams.filter?.dictionaryId,
      valueType: "select",
      valueEnum: dictionaryData?.results.reduce(
        (acc: Record<string, { text: string }>, dict: IDictionary) => {
          const id = dict.id ?? "unknown";
          acc[id] = { text: dict.name ?? "Unnamed" };
          return acc;
        },
        {}
      ),
      fieldProps: {
        value: selectedDictionaryId,
        onChange: (value: string) => {
          setSelectedDictionaryId(value);
          debouncedSetQueryParams({
            filter: { ...queryParams.filter, dictionaryId: value },
          });
        },
      },
    },
    {
      title: "Chinese character",
      dataIndex: "hanji",
      key: "hanji",
      initialValue: queryParams.filter?.hanji,
      fieldProps: {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          debouncedSetQueryParams({
            filter: { ...queryParams.filter, hanji: e.target.value },
          }),
      },
    },
    {
      title: "Romanisation",
      dataIndex: "roman",
      key: "roman",
      initialValue: queryParams.filter?.roman,
      fieldProps: {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          debouncedSetQueryParams({
            filter: { ...queryParams.filter, roman: e.target.value },
          }),
      },
    },
    {
      title: "Entry Type",
      dataIndex: "category",
      key: "category",
      initialValue: queryParams.filter?.category,
      fieldProps: {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          debouncedSetQueryParams({
            filter: { ...queryParams.filter, category: e.target.value },
          }),
      },
    },
    {
      title: "Category",
      dataIndex: "subCategory",
      key: "subCategory",
      initialValue: queryParams.filter?.subCategory,
      fieldProps: {
        onChange: (e: React.ChangeEvent<HTMLInputElement>) =>
          debouncedSetQueryParams({
            filter: { ...queryParams.filter, subCategory: e.target.value },
          }),
      },
    },
    {
      title: "Part of speech",
      dataIndex: ["meaning", 0, "partOfSpeech"],
      key: "partOfSpeech",
      search: false,
    },
    {
      title: "Hokkien definition",
      dataIndex: ["meaning", 0, "meaningHokkien"],
      key: "hokkien",
      search: false,
      width: 300,
    },
    {
      title: "Mandarin definition",
      dataIndex: ["meaning", 0, "meaningChinese"],
      key: "chinese",
      search: false,
      width: 300,
    },
    {
      title: "English definition",
      dataIndex: ["meaning", 0, "meaningEnglish"],
      key: "english",
      search: false,
      width: 300,
    },
    {
      title: "Malay definition",
      dataIndex: ["meaning", 0, "meaningMalay"],
      key: "malay",
      search: false,
      width: 300,
    },
    {
      title: "Indonesian definition",
      dataIndex: ["meaning", 0, "meaningIndonesia"],
      key: "indonesia",
      search: false,
      width: 300,
    },
    {
      title: "Filipino definition",
      dataIndex: ["meaning", 0, "meaningTagalog"],
      key: "tagalog",
      search: false,
      width: 300,
    },
    {
      title: "Actions",
      key: "action",
      search: false,
      fixed: "right",
      render: (_, record: IVocab) => (
        <div style={{ display: "flex", gap: "8px" }}>
          <Button
            icon={<FaEdit />}
            onClick={() => navigate(`/vocab/${record.id}`)}
            title="Edit"
          />
          <Popconfirm
            title="Are you sure you want to delete this entry?"
            onConfirm={() => record.id && handleArchive(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<FaTrash />} title="Delete" />
          </Popconfirm>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <Alert
        message="Error loading vocabularies"
        description={error.message}
        type="error"
      />
    );
  }

  return (
    <div style={{ paddingInline: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Title level={3}>Manage entries</Title>
        <Button
          type="primary"
          onClick={() => navigate("/vocab/new")}
          icon={<FaPlus />}
        >
          Create
        </Button>
      </div>
      <ProTable<IVocab>
        columns={columns}
        dataSource={data?.results}
        loading={isLoading}
        rowKey="_id"
        pagination={{
          total: data?.totalResults || 0,
          pageSize: 100,
          current: queryParams.params.current || 1,
          onChange: (page) => {
            setQueryParams((prev) => ({
              ...prev,
              params: {
                ...prev.params,
                current: page || 1,
                pageSize: 100,
              },
            }));
          },
          showSizeChanger: false,
        }}
        search={{
          labelWidth: "auto",
        }}
        onSubmit={(params) => {
          setQueryParams((prev) => ({
            ...prev,
            params: {
              current: 1,
              pageSize: 100,
            },
            filter: {
              dictionary: params.dictionaryId,
              category: params.category,
              hanji: params.hanji,
              roman: params.roman,
              subCategory: params.subCategory,
              wordIndexId: params.wordIndexId,
            },
          }));
        }}
        onReset={() => {
          setQueryParams({
            params: { current: 1, pageSize: 100 },
            filter: {},
          });
          setSelectedDictionaryId(undefined);
        }}
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};

export default VocabManagement;
